import React, { useState, useRef, useEffect, useCallback } from "react";
import { Filter } from "lucide-react";
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

export const Search = ({ onSearch, onFilter, searchQuery = "" }) => {
  const [query, setQuery] = useState(searchQuery);
  const [isFilterOpen, setFilterOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [indentType, setIndentType] = useState("");
  const searchInputRef = useRef(null);

  // Update local state when searchQuery prop changes
  useEffect(() => {
    setQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Create a debounced search function
  const debouncedSearch = useCallback(
    debounce((value) => {
      if (typeof onSearch === 'function') {
        onSearch(value);
      }
    }, 5),
    [onSearch]
  );

  // Handle input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value); // Update the input value immediately
    debouncedSearch(value); // Trigger the debounced search
  };

  const applyFilters = () => {
    if (typeof onFilter === 'function') {
      onFilter({ dateFrom, dateTo, indentType });
    }
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setIndentType("");
    if (typeof onFilter === 'function') {
      onFilter({ dateFrom: "", dateTo: "", indentType: "" });
    }
    setFilterOpen(false);
  };

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div className="relative">
      <div className="flex gap-2 items-center">
        <div className="relative flex-grow">
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={handleSearchChange}
            placeholder="Search..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm 
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                     text-gray-900 bg-white placeholder-gray-500"
            style={{ color: '#111827' }}
            autoComplete="off"
          />
        </div>
        <button
          type="button"
          onClick={() => setFilterOpen(!isFilterOpen)}
          className="bg-gray-300 p-2 rounded hover:bg-gray-400"
        >
          <Filter size={20} />
        </button>
      </div>

      {isFilterOpen && (
        <div className="absolute top-full right-0 mt-2 bg-white p-4 shadow-lg rounded-lg w-80 z-50 border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Filter Options</h3>
          <div className="flex flex-col gap-3">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">From Date:</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         text-gray-900 bg-white"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">To Date:</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         text-gray-900 bg-white"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Indent Type:</span>
              <select
                value={indentType}
                onChange={(e) => setIndentType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         text-gray-900 bg-white"
              >
                <option value="">All Types</option>
                <option value="Emergency">Emergency</option>
                <option value="Normal">Normal</option>
              </select>
            </label>
          </div>
          
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyFilters}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

Search.propTypes = {
  onSearch: PropTypes.func,
  onFilter: PropTypes.func,
  searchQuery: PropTypes.string
};

export default Search;